'use client'

import { getRelsAndTarget } from '@/utils/get-rel-and-target'

import { Text } from '@/components/Text/Text'
import type { TextProps } from '@/components/Text/Text'

export type HyperLinkProps = TextProps & {
    tag?: React.ElementType
    href?: string
    aria?: string
    target?: HTMLAnchorElement['target']
}

export function HyperLink({
    children,
    aria,
    onClick,
    href = '#',
    tag = 'a',
    target,
    rel,
    ...rest
}: HyperLinkProps) {
    const onLinkClicked = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (!onClick) return
        e.preventDefault()
        onClick(e)
    }
    const normHref = normalizeHref(href)
    const isLink = tag === 'a'
    let resolvedRel = rel || undefined
    let resolvedTarget = target || undefined
    if (!rel && isLink) {
        const { rels, target: proposedTarget } = getRelsAndTarget(normHref, resolvedTarget)
        resolvedRel = Array.from(rels).join(' ') || undefined
        resolvedTarget = proposedTarget
    }

    return (
        <Text
            tag={tag}
            aria-label={aria}
            onClick={onLinkClicked}
            href={tag === 'a' ? normHref : undefined}
            rel={resolvedRel}
            target={isLink ? resolvedTarget : undefined}
            {...rest}
        >
            {children}
        </Text>
    )
}

/** With the `www` migration, replace links that start with `https://todoist.com` with `https://www.todoist.com` for SEO purposes. */
const ignoreHrefPatterns = [
    'https://todoist.com/mac_app',
    'https://todoist.com/windows_app',
    'https://todoist.com/linux_app',
    'https://todoist.com/api',
    'https://todoist.com/research-interview',
    'https://todoist.com/slack/begin',
    'https://todoist.com/gcal/begin',
    'https://todoist.com/oauth',
    'https://todoist.com/slack/oauth',
]
function normalizeHref(href: string) {
    if (ignoreHrefPatterns.some((pattern) => href.toLowerCase().startsWith(pattern))) {
        return href
    }
    if (href.startsWith('https://todoist.com/app')) {
        const webAppURL = process.env.NEXT_PUBLIC_WEB_APP_URL || 'https://app.todoist.com'
        return href.replace('https://todoist.com/app', `${webAppURL}/app`)
    }
    if (href.startsWith('https://todoist.com')) {
        const landingURL = process.env.NEXT_PUBLIC_BASE_URL || 'https://www.todoist.com'
        return href.replace('https://todoist.com', landingURL)
    }
    return href
}
